import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, TrendingUp, TrendingDown, Download } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { TransactionFilters } from '../components/money/TransactionFilters';
import { TransactionList } from '../components/money/TransactionList';
import { ExportModal } from '../components/money/ExportModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Money() {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({ income: 0, expense: 0, net: 0, taxDeductible: 0 });
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const fetchTransactions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/transactions`, {
        withCredentials: true,
      });
      setTransactions(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      
      // Calculate stats
      let income = 0;
      let expense = 0;
      let taxDeductible = 0;
      data.forEach((txn) => {
        if (txn.type === 'income') {
          income += txn.amount;
        } else if (txn.type === 'invoice' && txn.invoice_status === 'paid') {
          income += txn.amount;
        } else if (txn.type === 'expense') {
          expense += txn.amount;
          if (txn.tax_deductible) {
            // Use deductible_amount if present (for meals), otherwise full amount
            const deductible = txn.deductible_amount || txn.amount;
            taxDeductible += deductible;
          }
        }
      });
      setStats({ income, expense, net: income - expense, taxDeductible });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleMarkPaid = async (txnId) => {
    try {
      await axios.post(
        `${API}/transactions/mark-paid`,
        { transaction_id: txnId },
        { withCredentials: true }
      );
      fetchTransactions();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error marking transaction paid:', error);
    }
  };

  const handleDeleteTransaction = async (txn) => {
    const label = txn.type === 'invoice' ? 'invoice' : txn.type;
    if (!window.confirm(`Delete ${label} "${txn.description}" for $${txn.amount.toFixed(2)}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/transactions/${txn.id}`, { withCredentials: true });
      fetchTransactions();
    } catch (err) {
      alert('Failed to delete transaction. Please try again.');
      if (process.env.NODE_ENV === 'development') console.error(err);
    }
  };

  const handleExport = useCallback(async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/reports/tax-deductible/${format}?year=${exportYear}`,
        {
          withCredentials: true,
          responseType: 'blob',
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax_deductible_expenses_${exportYear}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setShowExportModal(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [exportYear]);

  const filteredTransactions = transactions.filter((txn) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'income') return txn.type === 'income' || (txn.type === 'invoice' && txn.invoice_status === 'paid');
    if (activeFilter === 'expenses') return txn.type === 'expense';
    if (activeFilter === 'tax-deductible') return txn.type === 'expense' && txn.tax_deductible;
    if (activeFilter === 'invoices') return txn.type === 'invoice';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="max-w-2xl mx-auto p-4">
          <div className="text-text-secondary">Loading...</div>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-1" data-testid="money-title">
              Money
            </h1>
            <p className="text-text-secondary">{transactions.length} transactions</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowExportModal(true)}
              className="bg-secondary text-foreground hover:bg-secondary/80 border border-border font-medium rounded-lg px-4 py-2 flex items-center gap-2 transition-colors"
              data-testid="export-tax-report-button"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Tax Report</span>
            </button>
            <button
              onClick={() => navigate('/transactions/new')}
              className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 flex items-center gap-2 transition-colors"
              data-testid="add-transaction-button"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div data-testid="money-stat-income">
              <div className="flex items-center gap-1 mb-1">
                <TrendingUp className="w-4 h-4 text-success" />
                <span className="text-xs text-text-secondary uppercase tracking-wider">Income</span>
              </div>
              <div className="text-xl font-bold text-success">${stats.income.toFixed(2)}</div>
            </div>
            <div data-testid="money-stat-expense">
              <div className="flex items-center gap-1 mb-1">
                <TrendingDown className="w-4 h-4 text-alert" />
                <span className="text-xs text-text-secondary uppercase tracking-wider">Expenses</span>
              </div>
              <div className="text-xl font-bold text-alert">${stats.expense.toFixed(2)}</div>
            </div>
            <div data-testid="money-stat-net">
              <div className="text-xs text-text-secondary uppercase tracking-wider mb-1">Net</div>
              <div className={`text-xl font-bold ${stats.net >= 0 ? 'text-success' : 'text-alert'}`}>
                ${stats.net.toFixed(2)}
              </div>
            </div>
            <div data-testid="money-stat-tax-deductible">
              <div className="text-xs text-text-secondary uppercase tracking-wider mb-1">Tax Deduct.</div>
              <div className="text-xl font-bold text-primary">${stats.taxDeductible.toFixed(2)}</div>
              <div className="text-xs text-text-muted mt-1">This year (est.)</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs text-text-muted">
              Estimate only — consult a tax professional.
            </p>
          </div>
        </div>

        {/* Expense Templates Info Card */}
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-primary text-lg">💡</span>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-foreground mb-1">
                Quick Tip: Expense Templates Available
              </h3>
              <p className="text-sm text-text-secondary mb-2">
                Click <strong>Add</strong> → Select <strong>Expense</strong> → Choose from 40+ IRS-aligned categories including special mileage ($0.67/mile) and meals (50% deductible) calculators.
              </p>
            </div>
          </div>
        </div>

        <TransactionFilters 
          activeFilter={activeFilter} 
          onFilterChange={setActiveFilter} 
        />

        {filteredTransactions.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center" data-testid="money-empty">
            <p className="text-text-secondary mb-4">
              {activeFilter === 'all' ? 'No transactions yet' : `No ${activeFilter} transactions`}
            </p>
            {activeFilter === 'all' && (
              <button
                onClick={() => navigate('/transactions/new')}
                className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 transition-colors"
              >
                Add Your First Transaction
              </button>
            )}
          </div>
        ) : (
          <TransactionList 
            transactions={filteredTransactions} 
            onMarkPaid={handleMarkPaid}
            onDelete={handleDeleteTransaction}
          />
        )}
      </div>
      <Navigation />
      
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        exportYear={exportYear}
        onYearChange={setExportYear}
        onExport={handleExport}
        exporting={exporting}
      />
    </div>
  );
}
